import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAgentAuth } from '../../contexts/AgentAuthContext';
import AgentLayout from './AgentLayout';
import { Plus, ArrowRightLeft, Loader2, Download, Mail, Pencil, Trash2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_COLORS = {
  draft: 'bg-gray-100 text-gray-700',
  sent: 'bg-blue-100 text-blue-700',
  pending: 'bg-blue-100 text-blue-700',
  accepted: 'bg-green-100 text-green-700',
  rejected: 'bg-red-100 text-red-700',
  modification_requested: 'bg-amber-100 text-amber-700',
  expired: 'bg-gray-200 text-gray-500',
  converted_to_order: 'bg-purple-100 text-purple-700',
};

const AgentQuotes = () => {
  const { agentToken } = useAgentAuth();
  const navigate = useNavigate();
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [converting, setConverting] = useState(null);
  const [sendingEmail, setSendingEmail] = useState(null);

  const downloadPdf = async (q) => {
    try {
      const r = await fetch(`${API}/api/agent/quotes/${q.id}/pdf`, { headers: { Authorization: `Bearer ${agentToken}` } });
      if (!r.ok) { alert('Impossibile scaricare il PDF'); return; }
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `Preventivo_${q.quote_number}.pdf`;
      a.click(); URL.revokeObjectURL(url);
    } catch {
      alert('Errore download PDF');
    }
  };

  const sendEmail = async (q) => {
    if (!window.confirm(`Inviare il preventivo ${q.quote_number} via email a ${q.customer?.email || 'cliente'}?`)) return;
    setSendingEmail(q.id);
    try {
      const r = await fetch(`${API}/api/agent/quotes/${q.id}/send-email`, {
        method: 'POST', headers: { Authorization: `Bearer ${agentToken}` },
      });
      const j = await r.json();
      if (r.ok) alert(j.message || 'Email inviata');
      else alert(`Errore: ${j.detail || 'invio fallito'}`);
    } finally { setSendingEmail(null); }
  };

  const deleteQuote = async (q) => {
    if (!window.confirm(`Eliminare il preventivo ${q.quote_number}?`)) return;
    const r = await fetch(`${API}/api/agent/quotes/${q.id}`, {
      method: 'DELETE', headers: { Authorization: `Bearer ${agentToken}` },
    });
    const j = await r.json();
    if (r.ok) load();
    else alert(`Errore: ${j.detail || 'eliminazione fallita'}`);
  };

  const load = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/agent/quotes`, { headers: { Authorization: `Bearer ${agentToken}` } });
      const j = await r.json();
      setQuotes(j.data || []);
    } finally { setLoading(false); }
  };

  useEffect(() => { if (agentToken) load(); /* eslint-disable-next-line */ }, [agentToken]);

  const convertToOrder = async (q) => {
    if (!window.confirm(`Convertire il preventivo ${q.quote_number} in ordine? L'ordine verrà creato in stato "in attesa di pagamento".`)) return;
    setConverting(q.id);
    try {
      const r = await fetch(`${API}/api/agent/quotes/${q.id}/convert-to-order`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${agentToken}` },
      });
      const j = await r.json();
      if (r.ok) {
        alert(`Ordine creato: ${j.data.orderNumber}`);
        navigate('/agent/orders');
      } else {
        alert(`Errore: ${j.detail || 'conversione fallita'}`);
      }
    } finally { setConverting(null); }
  };

  const eur = (v) => Number(v || 0).toLocaleString('it-IT', { style: 'currency', currency: 'EUR' });

  return (
    <AgentLayout>
      <div className="mb-6 flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Preventivi</h1>
          <p className="text-sm text-gray-600 mt-1">{quotes.length} preventivi dei tuoi clienti.</p>
        </div>
        <button onClick={() => navigate('/agent/quotes/new')} data-testid="btn-new-quote"
          className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 text-sm font-medium">
          <Plus className="w-4 h-4" /> Nuovo preventivo
        </button>
      </div>
      <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
        {loading ? (
          <div className="py-16 text-center text-gray-500">Caricamento…</div>
        ) : quotes.length === 0 ? (
          <div className="py-16 text-center text-gray-500">Nessun preventivo in portafoglio.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-purple-50/50 text-purple-700 uppercase text-[10px]">
              <tr>
                <th className="text-left px-4 py-3">N°</th>
                <th className="text-left px-4 py-3">Cliente</th>
                <th className="text-left px-4 py-3">Stato</th>
                <th className="text-right px-4 py-3">Totale</th>
                <th className="text-left px-4 py-3">Data</th>
                <th className="text-right px-4 py-3">Azioni</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {quotes.map((q) => (
                <tr key={q.id} className="hover:bg-purple-50/40" data-testid={`quote-row-${q.id}`}>
                  <td className="px-4 py-3 font-mono text-xs text-purple-700">{q.quote_number}</td>
                  <td className="px-4 py-3">
                    <div className="text-gray-900">{q.customer?.business_name || `${q.customer?.firstName || ''} ${q.customer?.lastName || ''}`.trim()}</div>
                    <div className="text-[11px] text-gray-500">{q.customer?.email}</div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${STATUS_COLORS[q.status] || 'bg-gray-100 text-gray-700'}`}>
                      {q.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right font-semibold tabular-nums">{eur(q.total)}</td>
                  <td className="px-4 py-3 text-xs text-gray-500">
                    {q.created_at ? new Date(q.created_at).toLocaleDateString('it-IT') : '—'}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex items-center gap-1">
                      <button onClick={() => downloadPdf(q)} title="Scarica PDF"
                        data-testid={`btn-pdf-${q.id}`}
                        className="p-1.5 rounded hover:bg-purple-100">
                        <Download className="w-4 h-4 text-purple-600" />
                      </button>
                      <button onClick={() => sendEmail(q)} disabled={sendingEmail === q.id}
                        title="Invia email al cliente"
                        data-testid={`btn-email-${q.id}`}
                        className="p-1.5 rounded hover:bg-blue-100 disabled:opacity-50">
                        {sendingEmail === q.id
                          ? <Loader2 className="w-4 h-4 animate-spin text-blue-600" />
                          : <Mail className="w-4 h-4 text-blue-600" />}
                      </button>
                      {q.status !== 'converted_to_order' && q.status !== 'accepted' && (
                        <button onClick={() => navigate(`/agent/quotes/${q.id}/edit`)}
                          title="Modifica" data-testid={`btn-edit-quote-${q.id}`}
                          className="p-1.5 rounded hover:bg-amber-100">
                          <Pencil className="w-4 h-4 text-amber-600" />
                        </button>
                      )}
                      {q.status !== 'converted_to_order' && (
                        <button onClick={() => deleteQuote(q)} title="Elimina"
                          data-testid={`btn-delete-quote-${q.id}`}
                          className="p-1.5 rounded hover:bg-red-50">
                          <Trash2 className="w-4 h-4 text-red-600" />
                        </button>
                      )}
                      {q.status !== 'converted_to_order' && (
                        <button onClick={() => convertToOrder(q)} disabled={converting === q.id}
                          data-testid={`btn-convert-${q.id}`}
                          title="Converti in ordine"
                          className="inline-flex items-center gap-1 px-2 py-1 rounded text-[11px] font-medium bg-purple-100 text-purple-700 hover:bg-purple-200 disabled:opacity-50">
                          {converting === q.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <ArrowRightLeft className="w-3 h-3" />}
                          Converti
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </AgentLayout>
  );
};

export default AgentQuotes;
